import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { alpha, Box, Button, Chip, Divider, Stack, Typography, useTheme } from "@mui/material";
import { sectionIds } from "../../const/course";
import type { CourseDetailsPageData } from "../../hook/useCourseDetailsPageData";
import { getSectionTitle } from "../../util/course";
import { getCourseGlassSurfaceSx } from "./const/surface";
import RevealSection from "./RevealSection";
import SectionShell from "./SectionShell";

export type FactsSectionProps = Readonly<{
  data: CourseDetailsPageData;
}>;

export default function FactsSection({ data }: FactsSectionProps) {
  const theme = useTheme();

  return (
    <RevealSection>
      <SectionShell id="course-facts" title={getSectionTitle(data.pageMap, sectionIds.facts)}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 7fr) minmax(0, 5fr)" }, gap: 3 }}>
          <Stack
            spacing={0}
            sx={{
              p: { xs: 2.5, md: 3.5 },
              ...getCourseGlassSurfaceSx(theme),
            }}
          >
            {data.factsContent.facts.map((fact, index) => (
              <Box key={fact.key}>
                {index === 0 ? null : <Divider sx={{ borderColor: alpha(theme.palette.primary.contrastText, 0.08) }} />}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "32px 140px minmax(0, 1fr)" },
                    gap: 2,
                    alignItems: "center",
                    py: 2,
                  }}
                >
                  <FactIcon factKey={fact.key} />
                  <Typography variant="body2" sx={{ color: alpha(theme.palette.primary.contrastText, 0.64) }}>
                    {fact.label}
                  </Typography>
                  <Typography variant="body1" color="primary.contrastText">
                    {fact.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          <Stack
            spacing={3}
            sx={{
              p: { xs: 2.5, md: 3.5 },
              ...getCourseGlassSurfaceSx(theme, "strong"),
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h4" color="primary.contrastText">
                {data.factsContent.requirements.title}
              </Typography>
              <Chip
                label={data.factsContent.requirements.statusTag}
                variant="outlined"
                sx={{
                  borderRadius: 0,
                  borderColor: alpha(theme.palette.primary.contrastText, 0.22),
                  color: "primary.contrastText",
                  fontFamily: theme.typography.fontFamily,
                  "& .MuiChip-label": {
                    fontFamily: theme.typography.fontFamily,
                  },
                }}
              />
            </Stack>

            <Stack spacing={1.5}>
              {data.factsContent.requirements.items.map((item) => (
                <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                  <CheckCircleOutlineRoundedIcon sx={{ mt: "2px", color: alpha(theme.palette.info.light, 0.96) }} />
                  <Typography variant="body1" color="primary.contrastText">
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ borderColor: alpha(theme.palette.primary.contrastText, 0.12) }} />
            <Stack spacing={1.5}>
              <Typography variant="h5" color="primary.contrastText">
                {data.isEnded ? "課程已結束" : "下一步"}
              </Typography>
              <Typography variant="body1" sx={{ color: alpha(theme.palette.primary.contrastText, 0.72) }}>
                {data.isEnded
                  ? "此課程已結束，不再接受報名，你仍可查看過往屆別與相關內容。"
                  : `上課時間：${data.classTime}`}
              </Typography>
            </Stack>

            <Button
              variant="contained"
              onClick={data.footerAction.onClick}
              endIcon={<ArrowOutwardRoundedIcon />}
              sx={{
                width: { xs: 1, sm: "fit-content" },
                minHeight: 48,
                borderRadius: 0,
                bgcolor: "info.main",
                boxShadow: 6,
                "&:hover": { bgcolor: "info.dark", boxShadow: 8 },
              }}
            >
              {data.footerAction.label}
            </Button>
          </Stack>
        </Box>
      </SectionShell>
    </RevealSection>
  );
}

function FactIcon({ factKey }: Readonly<{ factKey: string }>) {
  const theme = useTheme();
  const sx = { color: alpha(theme.palette.info.main, 0.92) };
  if (factKey === "classTime") return <ScheduleOutlinedIcon sx={sx} />;
  if (factKey === "hours") return <WorkspacePremiumOutlinedIcon sx={sx} />;
  if (factKey === "location") return <LocationOnOutlinedIcon sx={sx} />;
  if (factKey === "price") return <PaymentsOutlinedIcon sx={sx} />;
  if (factKey === "contentNote") return <SchoolOutlinedIcon sx={sx} />;
  if (factKey === "language") return <LanguageOutlinedIcon sx={sx} />;
  return <Box />;
}
